import { DASH_META_ROOT, getAuthDashMetaCsrfToken } from '@revelio/auth';

import { consumeSse } from './global-ai-run-page-stream';
import { turnStepEventSchema } from './global-ai-turn-step-event.schema';

export type TurnToolCall = { id: string; name: string; argumentsJson: string };

export type TurnMessage =
  | { role: 'user'; content: string }
  | { role: 'assistant'; content: string }
  | { role: 'assistant'; content?: string; toolCalls: TurnToolCall[] }
  | { role: 'tool'; toolCallId: string; content: string };

export type TurnStepRequestBody = {
  messages: TurnMessage[];
  stepIndex: number;
  context?: {
    conversationSummary?: string;
    rolesTax?: string;
    skillsTax?: number;
    currentPage?: {
      pageKind?: string;
      canFilter?: boolean;
      matchedOptionId?: string;
      baseFilterPrompt?: string;
    };
    customDash?: {
      inCustomDashEditMode?: boolean;
      hasCurrentPageItems?: boolean;
    };
    /**
     * `__std__:` navigate option ids the account cannot access. The backend
     * strips these from the navigate candidate enum and pages grid so the model
     * never proposes or calls an inaccessible page (FE-2117).
     */
    deniedNavigateOptionIds?: string[];
  };
};

export type TurnStepEvent =
  | { type: 'text'; content: string }
  | { type: 'status'; content: string }
  | {
      type: 'artifact';
      id: string;
      kind: 'filter_response' | 'chart_config';
      payload: unknown;
    }
  | { type: 'tool_call'; id: string; name: string; input: unknown }
  | { type: 'tool_step'; name: string; phase: 'start' | 'end'; ok?: boolean }
  | { type: 'done' }
  | { type: 'error'; content: string };

export async function runTurnStep(params: {
  body: TurnStepRequestBody;
  onEvent: (event: TurnStepEvent) => void;
  onInvalidEnvelope?: (raw: string, reason: 'json' | 'schema') => void;
  signal?: AbortSignal;
}): Promise<void> {
  const csrfToken = getAuthDashMetaCsrfToken();
  const response = await fetch(
    `${DASH_META_ROOT}/api/build-a-dash/ai-gen/global-assistant/turn-step`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'x-surf-token-dash-meta': csrfToken ?? '',
      },
      body: JSON.stringify(params.body),
      signal: params.signal,
    }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `HTTP ${response.status}`);
  }
  // Every server step terminates with a `done` or `error` event. If the stream
  // ends without one (and we didn't abort it), the connection was cut mid-step
  // — surface it as an error so the loop ends the turn honestly instead of
  // reading the partial output as a complete, successful answer (FE-2117).
  let sawTerminal = false;
  await consumeSse(
    response,
    (line) => {
      if (!line.startsWith('data: ')) return;
      const raw = line.slice('data: '.length);
      let parsed: unknown;
      try {
        parsed = JSON.parse(raw);
      } catch {
        params.onInvalidEnvelope?.(raw, 'json');
        return;
      }
      const result = turnStepEventSchema.safeParse(parsed);
      if (!result.success) {
        params.onInvalidEnvelope?.(raw, 'schema');
        return;
      }
      const event = result.data as TurnStepEvent;
      if (event.type === 'done' || event.type === 'error') {
        sawTerminal = true;
      }
      params.onEvent(event);
    },
    params.signal
  );
  if (!params.signal?.aborted && !sawTerminal) {
    params.onEvent({
      type: 'error',
      content: 'The response was cut off before it finished. Please try again.',
    });
  }
}
